import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import Loader from 'components/Loader';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { getFilterOptionsFromClassifier } from 'components/tables/list-table/filterUtil';
import { FilterConfig, MainFilter } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo, useState } from 'react';
import { useLazyGetWipByCaseAndTimecardStatusReportQuery } from 'store/api/reportingOperations';
import { ClassifierData } from 'types/classifiers';
import { WipByCaseAndTimecardStatusRow, WipByCaseAndTimecardStatusTotals } from 'types/reporting/operations';
import { ReportPage } from 'types/reporting/reportPage';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';

import {
    ReportCaseLink,
    ReportContactLink,
    ReportDateCell,
    ReportEmployeeLink,
    ReportNumberCell,
    TONED_COLUMN_META,
    tonedCell,
    GREEN,
} from '../reportCells';
import {
    CASE_STATUS_ORDER,
    caseFilter,
    clientFilter,
    employeeFilter,
    inStatusOrder,
    optionsFilter,
    periodFilter,
    periodQuickFilter,
    quickFiltersFrom,
    searchSelectors,
    TIMECARD_STATUS_ORDER,
    mainFilterDefaults,
} from '../reportFilters';
import { rowCurrencies } from '../reportCurrency';
import { ReportLayout } from '../ReportLayout';
import { toneLegendOf } from '../ReportToneLegend';
import { ReportRowLimitNote } from '../ReportRowLimitNote';
import { ReportEntry } from '../reportCatalog';

type Row = WipByCaseAndTimecardStatusRow;
type Totals = WipByCaseAndTimecardStatusTotals;

const REPORT_KEY = 'wip-by-case-and-timecard-status';
const USED_CLASSIFIERS = ['TIMECARD_STATUS_TYPE', 'MATTER_STATE_TYPE', 'INVOICING_INTERVAL_TYPE'];
/** The WIP statuses, the report's starting status filter; clearing the filter shows every status. */
const DEFAULT_STATUSES = 'timecardStatus=in=(FINISHED,DRAFT_INVOICE)';

function buildFilterConfig(classifiers: Record<string, ClassifierData>): FilterConfig {
    const filters: MainFilter[] = [
        optionsFilter(
            'timecardStatus',
            'reporting.filter.timecard-status',
            inStatusOrder(getFilterOptionsFromClassifier(classifiers.TIMECARD_STATUS_TYPE), TIMECARD_STATUS_ORDER)
        ),
        clientFilter(),
        caseFilter(),
        optionsFilter(
            'caseState',
            'reporting.filter.case-status',
            inStatusOrder(getFilterOptionsFromClassifier(classifiers.MATTER_STATE_TYPE), CASE_STATUS_ORDER)
        ),
        employeeFilter('supervisingPartnerId', 'reporting.filter.supervising-partner'),
        employeeFilter('responsibleLawyerId', 'reporting.filter.responsible-lawyer'),
        periodFilter('reporting.filter.timecard-date-range'),
    ];
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        periodQuickFilter: periodQuickFilter(),
        quickFilters: quickFiltersFrom(filters, {
            timecardStatus: 'reporting.filter.timecard-status',
            caseState: 'reporting.filter.case-status',
        }),
        searchBarSelectors: searchSelectors('caseNumber', 'caseName', 'clientName'),
        filters,
    };
}

function figureColumn(
    id: 'nonBillableHours' | 'billableHours' | 'billableAmount' | 'expenseAmount',
    headerId: string,
    rows: Row[] | undefined
): DataTableColumnDef<Row, Totals> {
    return {
        id,
        accessorKey: id,
        header: t(headerId),
        meta: {
            ...TONED_COLUMN_META,
            widthWeight: 0.7,
            minPx: 120,
            textAlign: 'right',
            nullHandling: 'nullsLast',
            renderTotal:
                id === 'nonBillableHours' || id === 'billableHours'
                    ? (totals) => <ReportNumberCell value={totals?.[id]} />
                    : undefined,
        },
        cell: tonedCell(rows, (row) => row[id], GREEN),
    };
}

function buildColumns(rows: Row[] | undefined, classifiers: Record<string, ClassifierData>) {
    const columns: DataTableColumnDef<Row, Totals>[] = [
        {
            id: 'clientName',
            accessorKey: 'clientName',
            header: t('reporting.column.client-name'),
            meta: { widthWeight: 1.2, minPx: 160 },
            cell: ({ row }) => (
                <ReportContactLink partyId={row.original.clientId}>{row.original.clientName}</ReportContactLink>
            ),
        },
        {
            id: 'clientNumber',
            accessorKey: 'clientNumber',
            header: t('reporting.column.client-number'),
            meta: { widthWeight: 0.5, minPx: 90 },
            cell: ({ row }) => (
                <ReportContactLink partyId={row.original.clientId}>{row.original.clientNumber}</ReportContactLink>
            ),
        },
        {
            id: 'caseNumber',
            accessorKey: 'caseNumber',
            header: t('reporting.column.case-number'),
            meta: { widthWeight: 0.6, minPx: 110 },
            cell: ({ row }) => (
                <ReportCaseLink matterId={row.original.matterId} wrapLabel>
                    {row.original.caseNumber}
                </ReportCaseLink>
            ),
        },
        {
            id: 'caseName',
            accessorKey: 'caseName',
            header: t('reporting.column.case-name'),
            meta: { widthWeight: 1.4, minPx: 180 },
            cell: ({ row }) => (
                <ReportCaseLink matterId={row.original.matterId}>{row.original.caseName}</ReportCaseLink>
            ),
        },
        {
            id: 'caseState',
            accessorKey: 'caseStateCode',
            header: t('reporting.column.case-status'),
            meta: { widthWeight: 0.6, minPx: 110 },
            cell: ({ row }) => getClassifierText(classifiers.MATTER_STATE_TYPE, row.original.caseStateCode),
        },
        {
            id: 'supervisingPartner',
            accessorKey: 'supervisingPartner',
            header: t('reporting.column.supervising-partner'),
            meta: { widthWeight: 0.9, minPx: 140 },
            cell: ({ row }) => (
                <ReportEmployeeLink employeeId={row.original.supervisingPartnerId}>
                    {row.original.supervisingPartner}
                </ReportEmployeeLink>
            ),
        },
        {
            id: 'openedDate',
            accessorKey: 'openedDate',
            header: t('reporting.column.opened-date'),
            meta: { widthWeight: 0.6, minPx: 110 },
            cell: ({ row }) => <ReportDateCell value={row.original.openedDate} />,
        },
        {
            id: 'invoicingInterval',
            accessorKey: 'invoicingIntervalCode',
            header: t('reporting.column.invoicing-interval'),
            meta: { widthWeight: 0.6, minPx: 110 },
            cell: ({ row }) =>
                row.original.invoicingIntervalCode
                    ? getClassifierText(classifiers.INVOICING_INTERVAL_TYPE, row.original.invoicingIntervalCode)
                    : null,
        },
        {
            id: 'timecardStatus',
            accessorKey: 'timecardStatus',
            header: t('reporting.column.timecard-status'),
            meta: { widthWeight: 0.7, minPx: 120 },
            cell: ({ row }) => getClassifierText(classifiers.TIMECARD_STATUS_TYPE, row.original.timecardStatus),
        },
        {
            id: 'lastTimecardDate',
            accessorKey: 'lastTimecardDate',
            header: t('reporting.column.last-timecard-date'),
            meta: { widthWeight: 0.6, minPx: 110 },
            cell: ({ row }) => <ReportDateCell value={row.original.lastTimecardDate} />,
        },
        figureColumn('nonBillableHours', 'reporting.column.non-billable-hours', rows),
        figureColumn('billableHours', 'reporting.column.billable-hours', rows),
        figureColumn('billableAmount', 'reporting.column.billable-amount', rows),
        figureColumn('expenseAmount', 'reporting.column.expenses-amount', rows),
        {
            id: 'caseCurrency',
            accessorKey: 'caseCurrency',
            header: t('reporting.column.case-currency'),
            enableSorting: false,
            meta: {
                fixedPx: 130,
                renderTotal: () => <EllipsisTooltip>{rowCurrencies(rows, (row) => row.caseCurrency)}</EllipsisTooltip>,
            },
        },
    ];
    return columns;
}

export function WipByCaseAndTimecardStatusReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetWipByCaseAndTimecardStatusReportQuery();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });
    const [initialSearchParams] = useState(() => mainFilterDefaults([DEFAULT_STATUSES]));
    const filterConfig = useMemo(() => buildFilterConfig(classifiers), [classifiers]);
    const columns = useMemo(() => buildColumns(data?.content, classifiers), [data, classifiers]);
    return (
        <ReportLayout report={report} fillHeight toneLegend={toneLegendOf(columns)}>
            {isLoadingClassifiers ? (
                <Loader />
            ) : (
                <ListTableWithFilters<Row, ReportPage<Row, Totals>>
                    data={data}
                    belowQuickFilters={<ReportRowLimitNote report={report} rowCount={data?.content.length} />}
                    isLoading={isFetching}
                    isError={isError}
                    fetchData={fetchData}
                    filterConfig={filterConfig}
                    initialSearchParams={initialSearchParams}
                    columns={columns}
                    columnConfigId={`report-${REPORT_KEY}`}
                    enableColumnPicker
                    enableColumnReorder
                    enableColumnResize
                    pluralTextId="reporting.rows"
                    singularTextId="reporting.row"
                    noEntriesId="reporting.no-rows"
                    noEntriesBodyId="reporting.no-rows-body"
                />
            )}
        </ReportLayout>
    );
}
